/**
 * Store: Components
 * -----------------------------------------------------------------------------
 * Dynamic component states.
 * - Can be `waiting`, `loading`, or `active`.
 * - Components state can only go forwards, can't be reversed.
 * - Only JSDOC custom parameters, ignore module state or object.
 *
 */
import { defineStore } from 'pinia';
import useIndexStore from './index';

const useComponentsStore = defineStore('components', {
  state: () => ({}),

  getters: {
    /**
     * Get active components, i.e. ones which are `active`.
     * - Used by getComponentActive() helper function.
     * @returns {Array}
     */
    getActive(state) {
      const active = [];

      Object.keys(state).forEach((handle) => {
        if (!state[handle] || state[handle] !== 'active') {
          return;
        }

        active.push(handle);
      });

      return active;
    },

    /**
     * Get component if it exists.
     * @param {String} handle - Component handle.
     * @returns {Boolean}
     */
    getExists: (state) => {
      return (handle) => {
        return Boolean(state[handle]);
      };
    },

    /**
     * Get visible components, i.e. ones which are `loading` or `active`.
     * - Used by getComponentVisible() helper function.
     * @returns {Array}
     */
    getVisible(state) {
      const visible = [];

      Object.keys(state).forEach((handle) => {
        if (!state[handle] || state[handle] === 'waiting') {
          return;
        }

        visible.push(handle);
      });

      return visible;
    },

    /**
     * Get component state by handle.
     * @param {String} handle - Component handle.
     * @returns {Boolean}
     */
    getState: (state) => (handle) => {
      return state[handle];
    },
  },

  actions: {
    /**
     * Loads component.
     * - Used when manually triggering `trigger` type components.
     * @param {String} handle - Component handle.
     */
    load(handle) {
      if (this.getVisible.includes(handle) && !cnvs.request.designMode) {
        return;
      }

      this.setState({ handle, state: cnvs.states.loading });
      this.loadStylesheet(handle);
    },

    /**
     * Load component stylesheet.
     * @param {String} handle - Component handle.
     */
    // eslint-disable-next-line no-empty-pattern
    loadStylesheet(handle) {
      const indexStore = useIndexStore();

      if (cnvs.environment.mode === 'hot') {
        return;
      }

      const cssFile = `component.${handle}.min.css`;

      const cssFilepath = cnvs.urls.assets
        .replace('placeholder', cssFile)
        .replace(`v=${cnvs.urls.missingParameter}`, 'v=1793280307340');

      const cssUrl = cssFilepath.split('?')[0];

      /**
       * Go through all stylesheets to see if CSS has already been loaded.
       * - Aim is to avoid duplicate styles.
       */
      const stylesheets = [...document.querySelectorAll('[rel="stylesheet"]')];

      const matchedStylesheet = stylesheets.find((stylesheet) => {
        if (!stylesheet.href) {
          return false;
        }
        const href = stylesheet.href?.split('?')[0].replace('https:', '');

        return href === cssUrl;
      });

      if (matchedStylesheet) {
        return;
      }

      /**
       * No matched stylesheet so dynamically add to `<head>`.
       */
      const link = `<link href="${cssFilepath}" rel="stylesheet">`;
      document.head.insertAdjacentHTML('beforeend', link);

      /**
       * Emit event so loaded stylesheets can be tracked.
       */

      indexStore.setStylesheet(cssFile);
    },

    /**
     * Set component state.
     * @param {Object} data - Data object.
     * @param {String} data.handle - Component handle.
     * @param {String} data.state - Component's new state.
     */
    setState(data) {
      const { handle, state: newState } = data;
      const currentState = this[handle];

      if (!currentState) {
        this[handle] = newState;
      }

      switch (currentState) {
        case 'active':
          break;

        case 'loading':
          this[handle] = newState === 'active' ? newState : currentState;
          break;

        case 'waiting':
          this[handle] =
            newState === 'loading' || newState === 'active'
              ? newState
              : currentState;
      }
    },

    /**
     * Set components' states.
     * - Bulk sets states, can revert a component's state.
     * - Should only be called on hydration.
     * @param {Object} componentStates - Component states.
     */
    setStates(componentStates) {
      Object.keys(componentStates).forEach((handle) => {
        if (this[handle]) {
          return;
        }

        this[handle] = componentStates[handle];
      });
    },
  },
});

export default useComponentsStore;
